/**
 * PlanView - Unified plan page with AI chatbot
 *
 * Features:
 * - AI chatbot at the top for plan questions
 * - Unified tabs: Overview, Stories, Scenarios, Requirements, Export
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Text,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  BackIcon,
  EditIcon,
  IconButton,
} from '../design-system';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { usePlanContext } from '../hooks/api/usePlanContext';
import { useProcessingStage } from '../hooks/useProcessingStage';
import { useApp } from '../contexts/AppContext';
import { useSubscription } from '../contexts/SubscriptionContext';

import PlanSummary from '../components/PlanSummary';
import RefineModal from '../components/RefineModal';
import ExportModal from '../components/ExportModal';
import UnifiedEditModal from '../components/UnifiedEditModal';
import UpgradeModal, { UpgradePrompt } from '../components/UpgradeModal';

// Unified components
import DecisionsTab from '../components/tabs/DecisionsTab';
import { useIdea } from '../hooks/api/useIdeas';
import SystemFlowDiagram from '../components/SystemFlowDiagram';

import {
  extractGWTScenarios,
  groupStoriesByFeature,
} from '../utils/personaDataTransforms';
import apiClient from '../services/unifiedApiClient';

// ── Cottagecore design tokens ────────────────────────────────────────────────
const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  mossDark:  '#4E5A29',
  mossSoft:  '#D7DCB9',
  capRed:    '#C14A33',
  capRedSoft:'#F2C7BA',
  roof:      '#C76A3B',
  roofSoft:  '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Extract short display ID from globally unique ID
// e.g., "ba23b882-EP-2" -> "EP-2", "ba23b882-EP-2-US-9" -> "US-9"
const getDisplayId = (fullId) => {
  if (!fullId) return '';
  const idStr = String(fullId).trim();
  // Look for US-# pattern (story ID)
  const usMatch = idStr.match(/US-(\d+)/);
  if (usMatch) return `US-${usMatch[1]}`;
  // Look for EP-# pattern (feature ID)
  const epMatch = idStr.match(/EP-(\d+)/);
  if (epMatch) return `EP-${epMatch[1]}`;
  return fullId;
};

// Plan tabs - the main navigation for plan content
// Note: Overview is now a persistent header, not a tab
const PLAN_TABS = [
  { id: 'stories', label: 'User Stories', icon: '📖' },
  { id: 'scenarios', label: 'Test Scenarios', icon: '🧪' },
  { id: 'requirements', label: 'Requirements', icon: '📋' },
  { id: 'systemflow', label: 'System Flow', icon: '🔄' },
  { id: 'decisions', label: 'Decisions', icon: '⚙️' },
];

const PlanView = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { connected, connect, disconnect } = useWebSocket();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApp();
  const { canExport } = useSubscription();
  const posthog = usePostHog();

  // Restore last viewed tab for this plan
  const [activeTab, setActiveTab] = useState(() => {
    // Check for old subtab first (migration)
    const savedSubTab = localStorage.getItem(`plan-subtab-${taskId}`);
    if (savedSubTab && PLAN_TABS.some(t => t.id === savedSubTab)) {
      return savedSubTab;
    }
    const saved = localStorage.getItem(`plan-tab-${taskId}`);
    // Migrate old tab names - overview is now always visible, default to stories
    if (saved === 'engineering' || saved === 'product' || saved === 'breakdown' || saved === 'overview') return 'stories';
    // Check if it's a valid tab
    if (saved && PLAN_TABS.some(t => t.id === saved)) return saved;
    return 'stories';
  });

  // Scope toggle state (lifted from PMOverviewTab for shared access)
  const [excludedFeatures, setExcludedFeatures] = useState(new Set());
  const [excludedStories, setExcludedStories] = useState(new Set());
  // Track if premium panel is open (integrations or chat)
  const [activePremiumPanel, setActivePremiumPanel] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showRefineModal, setShowRefineModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('export');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, type: null, artifact: null });
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [whatNextDismissed, setWhatNextDismissed] = useState(
    () => localStorage.getItem(`plan-whatnext-dismissed-${taskId}`) === '1'
  );

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Connect to WebSocket on mount (once)
  // Note: We intentionally exclude connect/disconnect from deps to avoid reconnection loops
  // These functions internally check authentication state
  useEffect(() => {
    if (!isAuthenticated()) return;
    connect().catch(err => console.error('[PlanView] WebSocket error:', err));
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem(`plan-tab-${taskId}`, activeTab);
  }, [taskId, activeTab]);

  const {
    data: task,
    isLoading: taskLoading,
    error: taskError,
  } = useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: async () => {
      const response = await apiClient.get(`/plans/${taskId}`);
      return response?.data || response;
    },
    enabled: !!taskId && isAuthenticated(),
    // Poll every 3 seconds while processing to update progress tracker
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const isStillProcessing = status === 'submitted' || status === 'processing';
      return isStillProcessing ? 3000 : false;
    },
  });

  // Fetch idea info for breadcrumb navigation
  const ideaId = task?.idea_id;
  const { data: idea } = useIdea(ideaId, {
    enabled: !!ideaId,
  });

  const { data: taskTree, isLoading: treeLoading } = useQuery({
    queryKey: ['tasks', 'tree', taskId],
    queryFn: async () => {
      const response = await apiClient.get(`/plans/${taskId}/tree`);
      return response?.data || response;
    },
    enabled: !!taskId && isAuthenticated(),
    // Poll every 3 seconds while processing
    // Note: planContext/planStatus may not be available yet when this runs,
    // so we can't use them directly here. Instead, rely on WebSocket invalidation
    // and the polling logic in usePlanContext.
    refetchInterval: (query) => {
      // Simple polling: if tree has no data yet, keep polling
      // Once we have tree data, we rely on WebSocket invalidation
      const hasTreeData = query.state.data?.children?.length > 0 || query.state.data?.total_tasks > 0;
      const shouldPoll = !hasTreeData;
      return shouldPoll ? 3000 : false;
    },
    // Retry on error since tree might not exist initially during processing
    retry: 3,
    retryDelay: 1000,
  });

  // Get plan context which includes the plan status (Plans have statuses, Tasks do not)
  // This must come before isProcessing calculation since we use planContext there
  const { data: planContext, isLoading: contextLoading } = usePlanContext(taskId);

  // Use processing stage hook to track progress via WebSocket events
  const {
    currentPhase,
    hasFeatures,
    childTaskCount,
  } = useProcessingStage(taskId, task, taskTree, isReprocessing);

  // Determine processing state from plan context (Plans have statuses, Tasks do not)
  // NOTE: plan_status and is_processing come from /plans/{id}/context endpoint
  const planStatus = planContext?.plan_status;

  // Error state: generation ran and failed
  const isError = planStatus === 'error';

  // Ungenerated: plan shell created without triggering generation
  const isUngenerated = planStatus === 'submitted' && !isReprocessing;

  // Processing: actively being generated (or we just triggered reprocess)
  const isProcessing = isReprocessing ||
                       planStatus === 'processing' ||
                       (contextLoading && !planContext);

  // Clear isReprocessing once the backend confirms processing has started
  // Don't clear on 'completed' - that would be the old status before reprocess kicks in
  useEffect(() => {
    if (isReprocessing && (planStatus === 'processing' || planStatus === 'submitted')) {
      setIsReprocessing(false);
    }
  }, [planStatus, isReprocessing]);

  // When processing completes, force-refresh task detail and tree data
  // This ensures features/stories are loaded fresh after the progress screen dismisses
  const wasProcessingRef = React.useRef(isProcessing);
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      console.log('[PlanView] Processing completed, invalidating task queries');
      queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'tree', taskId] });
      queryClient.invalidateQueries({ queryKey: ['plans', 'context', taskId] });
      if (isError) {
        posthog?.capture('plan_failed', { plan_id: taskId });
      } else {
        posthog?.capture('plan_completed', { plan_id: taskId });
      }
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, isError, posthog, queryClient, taskId]);

  const handleRefresh = useCallback(() => {
    if (!connected) connect().catch(console.error);
    queryClient.invalidateQueries(['tasks', 'detail', taskId]);
    queryClient.invalidateQueries(['tasks', 'tree', taskId]);
  }, [connected, connect, queryClient, taskId]);

  // Callback for chat proposal approval -- refreshes plan data
  const handlePlanUpdate = useCallback(() => {
    queryClient.invalidateQueries(['tasks', 'detail', taskId]);
    queryClient.invalidateQueries(['tasks', 'tree', taskId]);
  }, [queryClient, taskId]);

  const handleResume = async () => {
    try {
      setIsReprocessing(true);
      // Use /generate for ungenerated/error plans, /reprocess for interrupted ones
      const endpoint = (isUngenerated || isError)
        ? `/plans/${taskId}/generate`
        : `/plans/${taskId}/reprocess`;
      await apiClient.post(endpoint);
      handleRefresh();
    } catch (error) {
      setIsReprocessing(false);
      console.error('Failed to resume:', error);
    }
  };

  // Toggle story status (Done/Undo)
  const handleToggleStatus = useCallback(async (type, id, currentStatus) => {
    const newStatus = currentStatus === 'implemented' ? 'todo' : 'implemented';
    const urlSegment = type === 'story' ? 'stories' : 'tasks';
    try {
      await apiClient.patch(`/plans/${taskId}/${urlSegment}/${id}/status`, {
        status: newStatus,
      });
      handleRefresh();
      showSuccess(`Story marked as ${newStatus === 'implemented' ? 'Done' : 'Todo'}`);
    } catch (error) {
      console.error('Failed to toggle status:', error);
      showError(error.userMessage || `Failed to update ${type} status`);
    }
  }, [taskId, handleRefresh, showSuccess, showError]);

  // Backfill missing task data
  // Edit handlers
  const handleEditArtifact = useCallback((artifactType, artifact) => {
    setEditModal({ open: true, type: artifactType, artifact });
  }, []);

  const handleEditComplete = useCallback(() => {
    setEditModal({ open: false, type: null, artifact: null });
    handleRefresh();
    showSuccess('Changes saved successfully!');
  }, [handleRefresh, showSuccess]);

  if (taskLoading) {
    return (
      <div style={styles.loadingContainer}>
        <LoadingSpinner size="lg" />
        <Text style={styles.loadingText}>Loading plan...</Text>
      </div>
    );
  }

  if (taskError || !task) {
    return (
      <div style={styles.container}>
        <ErrorMessage
          title="Plan not found"
          message={taskError?.message || 'This plan may have been deleted or you may not have access.'}
          onRetry={handleRefresh}
        />
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{
      ...styles.container,
    }}>
      {/* Header with Breadcrumb Navigation */}
      <header style={styles.header}>
        <div style={styles.breadcrumb}>
          <Button
            variant="ghost"
            size="sm"
            icon={BackIcon}
            onClick={() => navigate(idea ? `/ideas/${idea.id}` : '/dashboard')}
          >
            {idea?.name || 'Dashboard'}
          </Button>
          <span style={styles.breadcrumbSeparator}>/</span>
          <Text style={styles.breadcrumbCurrent}>
            {task?.name || task?.description?.slice(0, 40) || 'Plan'}
          </Text>
        </div>

        {!isProcessing && (
          <div style={styles.headerRight}>
            <Button
              variant={activePremiumPanel === 'integrations' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                if (!canExport()) {
                  setUpgradeFeature('export');
                  setShowUpgradeModal(true);
                  return;
                }
                setActivePremiumPanel(activePremiumPanel === 'integrations' ? null : 'integrations');
              }}
              title="Integrations"
            >
              <span style={isMobile ? {} : { marginRight: '4px' }}>🔗</span>
              {!isMobile && 'Integrations'}
              {!isMobile && <span style={styles.premiumBadge}>✦</span>}
            </Button>
          </div>
        )}
      </header>

      {/* Processing state - show progress during generation */}
      {isProcessing && (
        <PlanSummary
          task={task}
          taskTree={taskTree}
          onRefine={() => setShowRefineModal(true)}
          onResume={handleResume}
          isReprocessing={isReprocessing}
        />
      )}

      {/* Error state - generation failed */}
      {!isProcessing && isError && (
        <div style={{
          margin: '24px auto',
          maxWidth: 480,
          textAlign: 'center',
          padding: '32px',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
          <Text style={{ fontWeight: 600, color: T.ink, marginBottom: '8px' }}>
            Generation failed
          </Text>
          <Text style={{ color: T.inkMuted, fontSize: '14px', marginBottom: '20px' }}>
            Something went wrong while generating this plan's engineering breakdown. You can try again.
          </Text>
          <Button onClick={handleResume}>
            Try Again
          </Button>
        </div>
      )}

      {/* Ungenerated state - plan shell with no data yet */}
      {!isProcessing && !isError && isUngenerated && (
        <div style={{
          margin: '24px auto',
          maxWidth: 480,
          textAlign: 'center',
          padding: '32px',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✦</div>
          <Text style={{ fontWeight: 600, color: T.ink, marginBottom: '8px' }}>
            Ready to generate
          </Text>
          <Text style={{ color: T.inkMuted, fontSize: '14px', marginBottom: '20px' }}>
            Drag features into this plan from the Roadmap, then generate the engineering breakdown when ready.
          </Text>
          <Button onClick={handleResume}>
            Generate Now
          </Button>
        </div>
      )}

      {/* Persistent Overview Header - always visible when content exists */}
      {!isProcessing && !isError && !isUngenerated && (
        <PlanOverviewHeader
          task={task}
          taskTree={taskTree}
          idea={idea}
          excludedFeatures={excludedFeatures}
          excludedStories={excludedStories}
          onNavigateToIdea={() => ideaId && navigate(`/ideas/${ideaId}`)}
        />
      )}

      {/* Tab Nav - show after processing is complete and no error/ungenerated state */}
      {!isProcessing && !isError && !isUngenerated && (
        <div style={styles.tabNavContainer}>
          <div style={styles.tabNav}>
            {PLAN_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Only close integrations panel on tab switch; chat stays open as side panel
                  if (activePremiumPanel === 'integrations') {
                    setActivePremiumPanel(null);
                  }
                }}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === tab.id && activePremiumPanel !== 'integrations' ? styles.tabButtonActive : {}),
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      {!isProcessing && !isError && !isUngenerated && (
        <div style={styles.tabContent}>
          {/* What Next Banner - shown once after plan completes */}
          {!whatNextDismissed && planStatus === 'completed' && (
            <WhatNextBanner
              ideaId={ideaId}
              onDismiss={() => {
                localStorage.setItem(`plan-whatnext-dismissed-${taskId}`, '1');
                setWhatNextDismissed(true);
              }}
              onIntegrations={() => {
                if (!canExport()) {
                  setUpgradeFeature('export');
                  setShowUpgradeModal(true);
                  return;
                }
                setActivePremiumPanel('integrations');
              }}
            />
          )}

          {/* Sync Warning Banner - shows when parent idea spec was updated */}
          {(task?.needs_refresh || task?.context?.engineering_needs_refresh) && (
            <SyncWarningBanner
              taskId={taskId}
              onRegenerate={handleRefresh}
              ideaId={ideaId}
            />
          )}

          {/* Integrations panel - replaces tab content when active */}
          {activePremiumPanel === 'integrations' && (
            <div style={styles.premiumPanel}>
              <PMExportTab
                task={task}
                taskId={taskId}
                onShowToast={(msg, type) => type === 'error' ? showError(msg) : showSuccess(msg)}
              />
            </div>
          )}

          {/* Main Tab Content - show alongside chat, hide only for integrations */}
          {activePremiumPanel !== 'integrations' && (
            <div key={activeTab} className="tab-fade-in">
              {/* User Stories Tab */}
              {activeTab === 'stories' && (
                <PMOverviewTab
                  task={task}
                  idea={idea}
                  onEditArtifact={handleEditArtifact}
                  onToggleStatus={handleToggleStatus}
                  excludedFeatures={excludedFeatures}
                  excludedStories={excludedStories}
                  setExcludedFeatures={setExcludedFeatures}
                  setExcludedStories={setExcludedStories}
                />
              )}

              {/* Scenarios Tab */}
              {activeTab === 'scenarios' && (
                <DevScenariosTab task={task} onEditArtifact={handleEditArtifact} />
              )}

              {/* Requirements Tab */}
              {activeTab === 'requirements' && (
                <ConsultantRequirementsTab task={task} onEditArtifact={handleEditArtifact} />
              )}

              {/* System Flow Tab - Shows how the application works */}
              {activeTab === 'systemflow' && (
                <SystemFlowDiagram task={task} />
              )}

              {/* Decisions Tab - Engineering decisions made */}
              {activeTab === 'decisions' && (
                <DecisionsTab task={task} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <RefineModal
        isOpen={showRefineModal}
        onClose={() => setShowRefineModal(false)}
        task={task}
        taskTree={taskTree}
        onRefineComplete={handleRefresh}
      />

      <UnifiedEditModal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, type: null, artifact: null })}
        taskId={taskId}
        artifactType={editModal.type}
        artifact={editModal.artifact}
        onSaveComplete={handleEditComplete}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />

    </div>
  );
};

/* ========== PM TABS ========== */

// Persistent overview header - always visible above tabs
const PlanOverviewHeader = ({ task, taskTree, idea, excludedFeatures, excludedStories, onNavigateToIdea }) => {
  // Calculate metrics accounting for excluded scope
  const metrics = useMemo(() => {
    const allStories = task?.context?.user_stories || task?.metadata?.user_stories || [];
    const featureNames = new Set(allStories.map(s => s.feature_name).filter(Boolean));
    const totalFeatures = featureNames.size || (task?.context?.features?.length || 0);

    let includedStories = 0;
    allStories.forEach(story => {
      const featureExcluded = story.feature_name
        ? excludedFeatures.has(story.feature_name)
        : excludedFeatures.has(story.feature_id);
      const storyExcluded = excludedStories.has(story.id);
      if (!featureExcluded && !storyExcluded) includedStories++;
    });

    return {
      totalFeatures,
      totalStories: allStories.length,
      includedStories,
    };
  }, [task, taskTree, excludedFeatures, excludedStories]);

  // Scope display config
  const scope = task?.scope || 'mvp';
  const scopeDescription = task?.scope_description;
  const scopeConfig = {
    mvp: { label: 'MVP', icon: '🚀', description: 'Minimum viable product with core features' },
    full: { label: 'Full Release', icon: '📦', description: 'Complete implementation of all features' },
    feature: { label: 'Single Feature', icon: '🎯', description: 'Focus on one specific feature' },
    custom: { label: 'Custom Scope', icon: '⚙️', description: scopeDescription || 'Custom feature selection' },
  };
  const currentScope = scopeConfig[scope] || scopeConfig.mvp;

  // Check if anything is excluded
  const hasExclusions = excludedFeatures.size > 0 || excludedStories.size > 0;

  return (
    <div style={overviewHeaderStyles.container}>
      <div style={overviewHeaderStyles.grid}>
        {/* Scope Card */}
        <div style={overviewHeaderStyles.card}>
          <div style={overviewHeaderStyles.cardHeader}>
            <span style={overviewHeaderStyles.cardIcon}>{currentScope.icon}</span>
            <Text style={overviewHeaderStyles.cardTitle}>Scope</Text>
          </div>
          <Text style={overviewHeaderStyles.cardValue}>{currentScope.label}</Text>
          <Text style={overviewHeaderStyles.cardDesc}>{currentScope.description}</Text>
        </div>

        {/* Metrics Card */}
        <div style={overviewHeaderStyles.card}>
          <div style={overviewHeaderStyles.cardHeader}>
            <span style={overviewHeaderStyles.cardIcon}>📊</span>
            <Text style={overviewHeaderStyles.cardTitle}>Metrics</Text>
            {hasExclusions && (
              <Badge variant="warning" size="sm" style={{ marginLeft: 'auto' }}>
                Filtered
              </Badge>
            )}
          </div>
          <div style={overviewHeaderStyles.metricsGrid}>
            <div style={overviewHeaderStyles.metricItem}>
              <Text style={overviewHeaderStyles.metricValue}>{metrics.totalFeatures}</Text>
              <Text style={overviewHeaderStyles.metricLabel}>Features</Text>
            </div>
            <div style={overviewHeaderStyles.metricItem}>
              <Text style={overviewHeaderStyles.metricValue}>
                {hasExclusions ? (
                  <>{metrics.includedStories}<span style={overviewHeaderStyles.metricTotal}>/{metrics.totalStories}</span></>
                ) : metrics.totalStories}
              </Text>
              <Text style={overviewHeaderStyles.metricLabel}>Stories</Text>
            </div>
          </div>
        </div>

        {/* Parent Idea Card */}
        {idea && (
          <div style={overviewHeaderStyles.card}>
            <div style={overviewHeaderStyles.cardHeader}>
              <span style={overviewHeaderStyles.cardIcon}>📋</span>
              <Text style={overviewHeaderStyles.cardTitle}>Parent Idea</Text>
            </div>
            <Text style={overviewHeaderStyles.cardValue}>{idea.name?.replace(/^#+\s*/, '') || 'Untitled Idea'}</Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateToIdea}
              style={{ marginTop: '4px', padding: 0 }}
            >
              View Idea →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Styles for overview header
const overviewHeaderStyles = {
  container: {
    marginBottom: '12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  card: {
    backgroundColor: T.card,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r4,
    padding: '12px',
    boxShadow: T.sh1,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  cardIcon: {
    fontSize: '16px',
  },
  cardTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontFamily: T.sans,
    margin: 0,
  },
  cardValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: T.ink,
    margin: 0,
  },
  cardDesc: {
    fontSize: '14px',
    color: T.inkMuted,
    margin: 0,
    marginTop: '4px',
  },
  metricsGrid: {
    display: 'flex',
    gap: '16px',
  },
  metricItem: {
    textAlign: 'center',
  },
  metricValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: T.orange,
    margin: 0,
    lineHeight: 1.2,
  },
  metricTotal: {
    fontSize: '14px',
    fontWeight: '400',
    color: T.inkMuted,
  },
  metricLabel: {
    fontSize: '12px',
    color: T.inkMuted,
    margin: 0,
    textTransform: 'uppercase',
  },
};

const PMOverviewTab = ({ task, idea, onEditArtifact, onToggleStatus, excludedFeatures, excludedStories, setExcludedFeatures, setExcludedStories }) => {
  const featuresWithStories = groupStoriesByFeature(task, idea);
  const [expandedFeatures, setExpandedFeatures] = useState(() => {
    const initial = {};
    featuresWithStories.forEach((_, i) => { initial[i] = true; });
    return initial;
  });
  const [expandedStories, setExpandedStories] = useState({});

  // Reset expansion state when the task data changes
  useEffect(() => {
    const initial = {};
    featuresWithStories.forEach((_, i) => { initial[i] = true; });
    setExpandedFeatures(initial);
    setExpandedStories({});
  }, [task]);

  // Toggle feature scope (includes/excludes all its stories)
  const toggleFeatureScope = (featureId) => {
    setExcludedFeatures(prev => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
  };

  // Toggle individual story scope
  const toggleStoryScope = (storyId) => {
    setExcludedStories(prev => {
      const next = new Set(prev);
      if (next.has(storyId)) {
        next.delete(storyId);
      } else {
        next.add(storyId);
      }
      return next;
    });
  };

  const toggleFeature = (featureIndex) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureIndex]: !prev[featureIndex]
    }));
  };

  const toggleStory = (storyKey) => {
    setExpandedStories(prev => ({
      ...prev,
      [storyKey]: !prev[storyKey]
    }));
  };

  const expandAllFeatures = () => {
    const expanded = {};
    featuresWithStories.forEach((_, i) => { expanded[i] = true; });
    setExpandedFeatures(expanded);
  };

  const collapseAllFeatures = () => {
    setExpandedFeatures({});
  };

  // Helper to format story title
  const getStoryTitle = (story) => {
    if (story.title) return story.title;
    if (story.name) return story.name;
    if (story.actor && story.action) {
      return `As a ${story.actor}, ${story.action}`;
    }
    return 'Untitled Story';
  };

  // Get acceptance criteria from story
  const getAcceptanceCriteria = (story) => {
    if (story.acceptance_criteria && Array.isArray(story.acceptance_criteria)) {
      return story.acceptance_criteria;
    }
    if (story.gwt_scenarios && Array.isArray(story.gwt_scenarios)) {
      return story.gwt_scenarios.map(gwt =>
        `Given ${gwt.given}, when ${gwt.when}, then ${gwt.then}`
      );
    }
    return [];
  };

  // Calculate feature points
  const getFeaturePoints = (feature) => {
    return (feature.stories || []).reduce((sum, s) => sum + (s.story_points || 0), 0);
  };

  return (
    <Card style={styles.tabCard}>
      {/* Header with title and controls */}
      <div style={styles.storiesHeader}>
        <Text style={styles.tabTitle}>User Stories</Text>
        <div style={styles.storiesControls}>
          <Button variant="ghost" size="sm" onClick={expandAllFeatures}>Expand All</Button>
          <Button variant="ghost" size="sm" onClick={collapseAllFeatures}>Collapse All</Button>
        </div>
      </div>

      {featuresWithStories.length === 0 ? (
        <Text style={styles.emptyText}>No stories defined yet.</Text>
      ) : (
        <div style={styles.featureList}>
          {featuresWithStories.map((feature, i) => {
            const isFeatureExpanded = expandedFeatures[i];
            const storyCount = feature.stories?.length || 0;
            const featurePoints = getFeaturePoints(feature);
            const isFeatureExcluded = excludedFeatures.has(feature.id);
            const isFeatureShipped = feature.status === 'shipped';
            const isFeatureInProgress = feature.status === 'in_progress';
            const isFeatureBlocked = feature.status === 'blocked';

            return (
              <div
                key={i}
                style={{
                  ...styles.featureItem,
                  opacity: isFeatureExcluded ? 0.5 : isFeatureShipped ? 0.65 : 1,
                }}
              >
                <div style={styles.featureItemHeader}>
                  {/* Scope checkbox */}
                  <input
                    type="checkbox"
                    checked={!isFeatureExcluded}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleFeatureScope(feature.id);
                    }}
                    style={scopeStyles.checkbox}
                    title={isFeatureExcluded ? 'Include in scope' : 'Exclude from scope'}
                  />
                  <div
                    style={styles.featureHeaderLeft}
                    onClick={() => toggleFeature(i)}
                  >
                    <span style={styles.featureChevron}>
                      {isFeatureExpanded ? '▼' : '▶'}
                    </span>
                    <Text style={{
                      ...styles.featureName,
                      textDecoration: isFeatureExcluded ? 'line-through' : 'none',
                      fontStyle: isFeatureShipped ? 'italic' : 'normal',
                    }}>
                      {feature.name}
                    </Text>
                    {isFeatureShipped && (
                      <Badge variant="secondary" style={artifactStatusStyles.shippedBadge}>
                        Shipped
                      </Badge>
                    )}
                    {isFeatureInProgress && (
                      <Badge variant="secondary" style={artifactStatusStyles.inProgressBadge}>
                        In Progress
                      </Badge>
                    )}
                    {isFeatureBlocked && (
                      <Badge variant="secondary" style={artifactStatusStyles.blockedBadge}>
                        Blocked
                      </Badge>
                    )}
                    <Badge variant="secondary" style={styles.featureStoryCount}>
                      {storyCount} {storyCount === 1 ? 'story' : 'stories'}
                    </Badge>
                    {featurePoints > 0 && (
                      <Badge variant="secondary" style={scopeStyles.pointsBadge}>
                        {featurePoints} pts
                      </Badge>
                    )}
                  </div>
                </div>
                {isFeatureExpanded && (
                  <>
                    {feature.description && (
                      <Text style={styles.featureDesc}>{feature.description}</Text>
                    )}
                    {feature.stories && feature.stories.length > 0 ? (
                      <div style={styles.storyList}>
                        {feature.stories.map((story, j) => {
                          const storyKey = `${i}-${j}`;
                          const isExpanded = expandedStories[storyKey];
                          const criteria = getAcceptanceCriteria(story);
                          const hasCriteria = criteria.length > 0;
                          const isStoryExcluded = isFeatureExcluded || excludedStories.has(story.id);
                          const isStoryImplemented = story.status === 'implemented';
                          return (
                            <div
                              key={j}
                              style={{
                                ...styles.storyItemWrapper,
                                opacity: isStoryExcluded ? 0.5 : isStoryImplemented ? 0.65 : 1,
                              }}
                            >
                              <div
                                style={{
                                  ...styles.storyItem,
                                  cursor: hasCriteria ? 'pointer' : 'default',
                                }}
                              >
                                {/* Story scope checkbox */}
                                <input
                                  type="checkbox"
                                  checked={!isStoryExcluded}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleStoryScope(story.id);
                                  }}
                                  disabled={isFeatureExcluded}
                                  style={scopeStyles.checkbox}
                                  title={isStoryExcluded ? 'Include in scope' : 'Exclude from scope'}
                                />
                                <div
                                  style={styles.storyHeader}
                                  onClick={() => hasCriteria && toggleStory(storyKey)}
                                >
                                  {hasCriteria && (
                                    <span style={styles.storyChevron}>
                                      {isExpanded ? '▼' : '▶'}
                                    </span>
                                  )}
                                  {story.id && (
                                    <Badge variant="secondary" size="sm" style={styles.storyIdBadge}>{getDisplayId(story.id)}</Badge>
                                  )}
                                  <Text style={{
                                    ...styles.storyTitle,
                                    textDecoration: isStoryExcluded || isStoryImplemented ? 'line-through' : 'none',
                                  }}>
                                    {getStoryTitle(story)}
                                  </Text>
                                  {isStoryImplemented && (
                                    <Badge variant="secondary" size="sm" style={artifactStatusStyles.implementedBadgeSm}>
                                      Done
                                    </Badge>
                                  )}
                                  {story.story_points > 0 && (
                                    <Badge variant="secondary" size="sm" style={scopeStyles.storyPointsBadge}>
                                      {story.story_points} pts
                                    </Badge>
                                  )}
                                </div>
                                <div style={styles.storyActions}>
                                  {hasCriteria && (
                                    <Badge style={styles.criteriaBadge}>
                                      {criteria.length} criteria
                                    </Badge>
                                  )}
                                  {onToggleStatus && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleStatus('story', story.id, story.status);
                                      }}
                                      style={artifactStatusStyles.toggleButton}
                                    >
                                      {isStoryImplemented ? 'Undo' : 'Done'}
                                    </Button>
                                  )}
                                  {onEditArtifact && (
                                    <IconButton
                                      icon={EditIcon}
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onEditArtifact('story', story);
                                      }}
                                      title="Edit story"
                                    />
                                  )}
                                </div>
                              </div>
                              {isExpanded && hasCriteria && (
                                <div style={styles.criteriaContainer}>
                                  <Text style={styles.criteriaLabel}>Acceptance Criteria:</Text>
                                  <ul style={styles.criteriaList}>
                                    {criteria.map((criterion, k) => (
                                      <li key={k} style={styles.criteriaItem}>
                                        {typeof criterion === 'string'
                                          ? criterion
                                          : criterion.description || JSON.stringify(criterion)
                                        }
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Text style={styles.noStoriesText}>No stories assigned</Text>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// Styles for scope toggle checkboxes
const scopeStyles = {
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    flexShrink: 0,
    marginRight: '8px',
    accentColor: T.orange,
  },
  pointsBadge: {
    marginLeft: '8px',
    backgroundColor: T.mossSoft,
    color: T.mossDark,
    borderRadius: T.full,
  },
  storyPointsBadge: {
    marginLeft: '8px',
    backgroundColor: T.paperDeep,
    color: T.inkMuted,
    fontSize: '12px',
    borderRadius: T.full,
  },
};

// Styles for feature/story status badges
const artifactStatusStyles = {
  shippedBadge: {
    marginLeft: '8px',
    backgroundColor: T.mossSoft,
    color: T.mossDark,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: T.full,
  },
  inProgressBadge: {
    marginLeft: '8px',
    backgroundColor: T.roofSoft,
    color: T.orangeDark,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: T.full,
  },
  blockedBadge: {
    marginLeft: '8px',
    backgroundColor: T.capRedSoft,
    color: T.capRed,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: T.full,
  },
  implementedBadgeSm: {
    marginLeft: '4px',
    backgroundColor: T.mossSoft,
    color: T.mossDark,
    fontSize: '10px',
    fontWeight: 500,
    padding: '1px 6px',
    borderRadius: T.full,
  },
  summaryImplementedBadge: {
    backgroundColor: T.mossSoft,
    color: T.mossDark,
    marginRight: '8px',
    borderRadius: T.full,
  },
  toggleButton: {
    fontSize: '12px',
    padding: `2px ${'8px'}`,
    minHeight: 'unset',
    lineHeight: 1.4,
    color: T.inkFaint,
    opacity: 0.8,
  },
};

const PMExportTab = ({ task, taskId, onShowToast }) => {
  const { canExport } = useSubscription();

  if (!canExport()) {
    return (
      <Card style={styles.tabCard}>
        <Text style={styles.tabTitle}>Integrations</Text>
        <UpgradePrompt feature="export" />
      </Card>
    );
  }

  return (
    <Card style={styles.tabCard}>
      <Text style={styles.tabTitle}>Integrations</Text>
      <Text style={styles.exportDesc}>
        Push your plan to external project management tools.
      </Text>

      {/* Project Management Tools */}
      <div style={styles.exportSection}>
        <div style={styles.exportSectionHeader}>
          <Text style={styles.exportSectionTitle}>Project Management</Text>
          <Text style={styles.exportSectionDesc}>
            Create a new project in JIRA or Trello with your features and stories.
          </Text>
        </div>
        <ExportModal
          isOpen={true}
          onClose={() => {}}
          task={task}
          planId={taskId}
          features={task?.context?.features || []}
          onShowToast={onShowToast}
          inline={true}
        />
      </div>
    </Card>
  );
};

/* ========== DEV TABS ========== */

const DevScenariosTab = ({ task, onEditArtifact }) => {
  const gwtScenarios = extractGWTScenarios(task);

  // Stories are in context, not metadata
  const stories = task?.context?.user_stories || task?.metadata?.user_stories || [];

  // Build test scenarios from acceptance criteria
  const buildTestScenarios = () => {
    const scenarios = [];
    stories.forEach((story) => {
      const storyTitle = story.title || story.name ||
        (story.actor && story.action ? `As a ${story.actor}, ${story.action}` : 'Untitled');

      const criteria = Array.isArray(story.acceptance_criteria)
        ? story.acceptance_criteria
        : story.acceptance_criteria ? [story.acceptance_criteria] : [];

      criteria.forEach((ac) => {
        scenarios.push({
          storyTitle,
          story, // Include full story for editing
          criterion: typeof ac === 'string' ? ac : ac.description || JSON.stringify(ac),
        });
      });
    });
    return scenarios;
  };

  const testScenarios = gwtScenarios.length > 0 ? null : buildTestScenarios();
  const hasGWT = gwtScenarios.length > 0;

  return (
    <Card style={styles.tabCard}>
      <div style={styles.scenariosHeader}>
        <Text style={styles.tabTitle}>Test Scenarios</Text>
        <Badge variant="success">{hasGWT ? gwtScenarios.length : (testScenarios?.length || 0)} scenarios</Badge>
      </div>
      {hasGWT ? (
        <div style={styles.scenarioList}>
          {gwtScenarios.map((s, i) => (
            <div key={i} style={styles.scenarioItem}>
              <div style={styles.scenarioHeader}>
                <Text style={styles.scenarioStory}>{s.storyTitle}</Text>
                {onEditArtifact && (
                  <IconButton
                    icon={EditIcon}
                    size="sm"
                    variant="ghost"
                    onClick={() => onEditArtifact('gwt', { ...s, id: `gwt_${i}` })}
                    title="Edit scenario"
                  />
                )}
              </div>
              <div style={styles.gwtBlock}>
                <div style={styles.gwtRow}>
                  <span style={styles.gwtLabel}>Given</span>
                  <Text style={styles.gwtText}>{s.given}</Text>
                </div>
                <div style={styles.gwtRow}>
                  <span style={styles.gwtLabel}>When</span>
                  <Text style={styles.gwtText}>{s.when}</Text>
                </div>
                <div style={styles.gwtRow}>
                  <span style={styles.gwtLabel}>Then</span>
                  <Text style={styles.gwtText}>{s.then}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : testScenarios && testScenarios.length > 0 ? (
        <div style={styles.scenarioList}>
          {testScenarios.map((scenario, i) => (
            <div key={i} style={styles.testScenarioItem}>
              <div style={styles.scenarioHeader}>
                <Text style={styles.scenarioStory}>{scenario.storyTitle}</Text>
                {onEditArtifact && (
                  <IconButton
                    icon={EditIcon}
                    size="sm"
                    variant="ghost"
                    onClick={() => onEditArtifact('story', scenario.story)}
                    title="Edit story"
                  />
                )}
              </div>
              <div style={styles.testCriterion}>
                <span style={styles.testIcon}>✓</span>
                <Text style={styles.testText}>{scenario.criterion}</Text>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyScenarios}>
          <span style={styles.emptyScenariosIcon}>📋</span>
          <Text style={styles.emptyText}>No test scenarios defined yet</Text>
        </div>
      )}
    </Card>
  );
};


/* ========== REQUIREMENTS TAB ========== */

const ConsultantRequirementsTab = ({ task, onEditArtifact }) => {
  const requirements = task?.metadata?.requirements || {};
  const functional = requirements.functional_requirements || [];
  const nonFunctional = requirements.non_functional_requirements || [];

  const priorityColors = {
    'Must Have': T.capRed,
    'Should Have': T.orange,
    'Could Have': T.moss,
  };

  if (functional.length === 0 && nonFunctional.length === 0) {
    return (
      <Card style={styles.tabCard}>
        <Text style={styles.tabTitle}>Requirements</Text>
        <Text style={styles.emptyText}>No requirements generated yet. Requirements are generated during task analysis.</Text>
      </Card>
    );
  }

  return (
    <Card style={styles.tabCard}>
      <Text style={styles.tabTitle}>Requirements</Text>

      {functional.length > 0 && (
        <div style={styles.reqSection}>
          <Text style={styles.reqSectionTitle}>Functional Requirements</Text>
          <div style={styles.reqList}>
            {functional.map((req, i) => (
              <div key={i} style={styles.reqItem}>
                <div style={styles.reqHeader}>
                  <Badge variant="secondary" style={styles.reqId}>{req.id}</Badge>
                  <Badge style={{
                    ...styles.reqCategory,
                    backgroundColor: `${T.moss}20`,
                    color: T.moss
                  }}>{req.category}</Badge>
                  <Text style={{
                    ...styles.reqPriority,
                    color: priorityColors[req.priority] || T.inkFaint
                  }}>{req.priority}</Text>
                  {onEditArtifact && (
                    <IconButton
                      icon={EditIcon}
                      size="sm"
                      variant="ghost"
                      onClick={() => onEditArtifact('requirement', req)}
                      title="Edit requirement"
                      style={styles.reqEditButton}
                    />
                  )}
                </div>
                <Text style={styles.reqText}>{req.requirement}</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {nonFunctional.length > 0 && (
        <div style={styles.reqSection}>
          <Text style={styles.reqSectionTitle}>Non-Functional Requirements</Text>
          <div style={styles.reqList}>
            {nonFunctional.map((req, i) => (
              <div key={i} style={styles.reqItem}>
                <div style={styles.reqHeader}>
                  <Badge variant="secondary" style={styles.reqId}>{req.id}</Badge>
                  <Badge style={{
                    ...styles.reqCategory,
                    backgroundColor: `${T.inkMuted}20`,
                    color: T.inkMuted
                  }}>{req.category}</Badge>
                  <Text style={{
                    ...styles.reqPriority,
                    color: priorityColors[req.priority] || T.inkFaint
                  }}>{req.priority}</Text>
                  {onEditArtifact && (
                    <IconButton
                      icon={EditIcon}
                      size="sm"
                      variant="ghost"
                      onClick={() => onEditArtifact('non_functional_requirement', req)}
                      title="Edit requirement"
                      style={styles.reqEditButton}
                    />
                  )}
                </div>
                <Text style={styles.reqText}>{req.requirement}</Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

/* ========== HELPERS ========== */

// ── What Next Banner ──────────────────────────────────────────────────────────

const WhatNextBanner = ({ ideaId, onDismiss, onIntegrations }) => {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: '16px',
      padding: '16px 20px',
      marginBottom: '16px',
      borderRadius: T.r4,
      border: `1px solid ${T.ruleStrong}`,
      backgroundImage: `linear-gradient(135deg, ${T.mossSoft} 0%, ${T.paperSoft} 100%)`,
    }}>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '22px', flexShrink: 0, marginTop: '1px' }}>✦</span>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: T.ink,
            marginBottom: '4px',
          }}>
            Your plan is ready — here's what to do next
          </div>
          <div style={{
            fontSize: '14px',
            color: T.inkMuted,
            lineHeight: '1.5',
          }}>
            Download the AI Starter Bundle and drop it in your repo so your AI coding agent can start building, or push stories directly to Jira.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
          <button
            onClick={() => ideaId && navigate(`/ideas/${ideaId}?tab=download`)}
            style={{
              padding: '8px 16px',
              backgroundColor: T.moss,
              border: 'none',
              borderRadius: T.r3,
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = T.mossDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = T.moss; }}
          >
            Download AI Starter Bundle
          </button>
          <button
            onClick={onIntegrations}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: `1px solid ${T.ruleStrong}`,
              borderRadius: T.r3,
              color: T.mossDark,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.moss; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.ruleStrong; }}
          >
            Push to Jira ↗
          </button>
        </div>
      </div>
      <button
        onClick={onDismiss}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: T.inkFaint, padding: '2px', flexShrink: 0,
          display: 'flex', alignItems: 'center',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.inkMuted; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

// ── Sync Warning Banner ───────────────────────────────────────────────────────

const SyncWarningBanner = ({ taskId, onRegenerate, ideaId }) => {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await apiClient.post(`/plans/${taskId}/advance`, {
        action: 'ready_for_engineering',
      });
      onRegenerate?.();
    } catch (error) {
      console.error('Failed to regenerate engineering:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isDismissed) return null;

  return (
    <div style={styles.syncWarningBanner}>
      <div style={styles.syncWarningContent}>
        <span style={styles.syncWarningIcon}>⚠️</span>
        <div style={styles.syncWarningText}>
          <Text style={styles.syncWarningTitle}>Specification has been updated</Text>
          <Text style={styles.syncWarningDesc}>
            The parent idea's specification was modified. Re-decompose to sync this plan with the latest spec.
          </Text>
        </div>
      </div>
      <div style={styles.syncWarningActions}>
        {ideaId && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/ideas/${ideaId}`)}
          >
            View Idea
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={handleRegenerate}
          loading={isRegenerating}
        >
          Re-decompose
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsDismissed(true)}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};

/* ========== STYLES ========== */

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '16px',
  },
  loadingText: {
    color: T.inkMuted,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
  },
  breadcrumbSeparator: {
    color: T.inkFaint,
    fontSize: '14px',
    padding: '0 4px',
  },
  breadcrumbCurrent: {
    fontSize: '14px',
    fontWeight: '500',
    color: T.ink,
    padding: '8px 12px',
  },
  tabNavContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  tabNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingBottom: '8px',
    borderBottom: `1px dashed ${T.rule}`,
  },
  tabActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    border: '1px solid transparent',
    borderRadius: T.r3,
    backgroundColor: 'transparent',
    color: T.ink2,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: T.sans,
  },
  tabButtonActive: {
    backgroundColor: T.card,
    borderColor: T.rule,
    color: T.ink,
    boxShadow: T.sh1,
  },
  premiumBadge: {
    fontSize: '10px',
    color: T.inkFaint,
    marginLeft: '4px',
  },
  premiumPanel: {
    marginBottom: '16px',
  },
  tabContent: {
    minHeight: '200px',
  },
  tabCard: {
    padding: '24px',
  },
  tabTitle: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '16px',
    color: T.ink,
    fontFamily: T.sans,
  },
  emptyText: {
    color: T.inkMuted,
    fontStyle: 'italic',
  },
  exportDesc: {
    color: T.inkMuted,
    marginBottom: '24px',
  },
  exportSection: {
    marginBottom: '24px',
  },
  exportSectionHeader: {
    marginBottom: '16px',
  },
  exportSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: T.ink,
    marginBottom: '8px',
  },
  exportSectionDesc: {
    fontSize: '14px',
    color: T.inkMuted,
    lineHeight: '1.6',
  },
  exportButtonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },
  exportFileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: T.paperDeep,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r4,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },
  exportFileIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  exportFileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  exportFileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: T.ink,
  },
  exportFileDesc: {
    fontSize: '12px',
    color: T.inkMuted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // PM styles
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  featureItem: {
    padding: '16px',
    backgroundColor: T.card,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    boxShadow: T.sh1,
  },
  storiesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  storiesControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  storiesSummaryBadge: {
    padding: '4px 12px',
    fontSize: '12px',
  },
  featureItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '8px',
    marginLeft: `-${'8px'}`,
    marginRight: `-${'8px'}`,
    marginTop: `-${'8px'}`,
    borderRadius: T.r3,
    transition: 'background-color 0.15s ease',
  },
  featureHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  featureChevron: {
    fontSize: '14px',
    color: T.orange,
    width: '16px',
    flexShrink: 0,
    fontWeight: '700',
  },
  featureName: {
    fontSize: '18px',
    fontWeight: '600',
  },
  featureStoryCount: {
    fontSize: '12px',
    padding: '4px 8px',
  },
  featureDesc: {
    fontSize: '14px',
    color: T.inkMuted,
    marginBottom: '12px',
  },
  storyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingLeft: '16px',
    borderLeft: `2px solid ${T.rule}`,
  },
  storyItemWrapper: {
    marginBottom: '8px',
  },
  storyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderRadius: T.r3,
    transition: 'background-color 0.15s ease',
  },
  storyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
  },
  storyChevron: {
    fontSize: '12px',
    color: T.inkMuted,
    width: '12px',
  },
  storyIdBadge: {
    fontFamily: T.mono,
    fontSize: '11px',
    backgroundColor: T.roofSoft,
    color: T.orange,
    borderRadius: T.full,
    flexShrink: 0,
  },
  storyTitle: {
    fontSize: '14px',
  },
  storyActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  criteriaBadge: {
    fontSize: '12px',
    padding: '4px 8px',
    backgroundColor: T.paperDeep,
  },
  criteriaContainer: {
    marginLeft: '24px',
    marginTop: '8px',
    padding: '12px',
    backgroundColor: T.paperSoft,
    borderRadius: T.r3,
    border: `1px solid ${T.rule}`,
  },
  criteriaLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  noStoriesText: {
    fontSize: '14px',
    color: T.inkMuted,
    fontStyle: 'italic',
    paddingLeft: '16px',
  },
  // PM Tasks Tab styles
  tasksHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  tasksControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  sortSelect: {
    padding: '8px 12px',
    borderRadius: T.r3,
    border: `1px solid ${T.rule}`,
    backgroundColor: T.paperDeep,
    color: T.ink,
    fontSize: '14px',
    cursor: 'pointer',
  },
  tasksSummary: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  tasksSummaryBadge: {
    padding: '8px 16px',
    fontSize: '14px',
  },
  tasksEmpty: {
    padding: '32px',
    textAlign: 'center',
  },
  tasksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  taskCard: {
    backgroundColor: T.card,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    boxShadow: T.sh1,
    overflow: 'hidden',
  },
  taskCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    gap: '12px',
    transition: 'background-color 0.15s ease',
  },
  taskCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  taskNumber: {
    fontSize: '14px',
    fontWeight: '700',
    color: T.orange,
    fontFamily: T.mono,
    minWidth: '36px',
  },
  taskExpandIcon: {
    fontSize: '12px',
    color: T.inkMuted,
    width: '12px',
    flexShrink: 0,
  },
  taskCardDescription: {
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '1.6',
    flex: 1,
  },
  taskCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  taskDepIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: `${T.roof}15`,
    borderRadius: T.r3,
    cursor: 'help',
  },
  taskDepIcon: {
    fontSize: '14px',
    color: T.roof,
  },
  taskDepCount: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.orangeDark,
  },
  taskPointsBadge: {
    fontSize: '12px',
    padding: '4px 12px',
    fontFamily: 'monospace',
    fontWeight: '600',
  },
  taskCardDetails: {
    padding: '16px',
    paddingTop: 0,
    borderTop: `1px solid ${T.rule}`,
    marginTop: '8px',
  },
  taskDepSection: {
    marginBottom: '16px',
  },
  taskDepLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  taskDepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  taskDepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px',
    backgroundColor: T.paper,
    borderRadius: T.r3,
    borderLeft: `3px solid ${T.roof}`,
  },
  taskDepRef: {
    fontSize: '12px',
    fontWeight: '700',
    color: T.orangeDark,
    fontFamily: T.mono,
    minWidth: '32px',
  },
  taskDepText: {
    fontSize: '12px',
    color: T.inkMuted,
    flex: 1,
  },
  taskImplSection: {
    marginTop: '12px',
  },
  taskImplLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  taskImplText: {
    fontSize: '14px',
    color: T.ink,
    lineHeight: '1.6',
    padding: '12px',
    backgroundColor: T.paper,
    borderRadius: T.r3,
    fontFamily: T.mono,
    whiteSpace: 'pre-wrap',
  },
  // Dev styles
  modelSection: {
    marginBottom: '16px',
  },
  sectionHeader: {
    fontSize: '14px',
    fontWeight: '600',
    color: T.inkMuted,
    marginBottom: '8px',
  },
  badgeGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  editableBadge: {
    position: 'relative',
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
  },
  editHint: {
    position: 'absolute',
    bottom: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '12px',
    color: T.inkMuted,
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },
  scenariosHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  scenarioList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  scenarioItem: {
    padding: '16px',
    backgroundColor: T.paperDeep,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    borderLeft: `3px solid ${T.moss}`,
  },
  scenarioHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  scenarioStory: {
    fontSize: '14px',
    fontWeight: '500',
    color: T.ink,
  },
  gwtBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    backgroundColor: T.paperSoft,
    borderRadius: T.r3,
    padding: '12px',
  },
  gwtRow: {
    display: 'flex',
    gap: '12px',
  },
  gwtLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.moss,
    textTransform: 'uppercase',
    minWidth: '50px',
    flexShrink: 0,
  },
  gwtText: {
    fontSize: '14px',
    color: T.ink,
    lineHeight: 1.4,
  },
  gwtLine: {
    fontSize: '14px',
  },
  testScenarioItem: {
    padding: '16px',
    backgroundColor: T.paperSoft,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    borderLeft: `3px solid ${T.moss}`,
  },
  emptyScenarios: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
    textAlign: 'center',
  },
  emptyScenariosIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  testCriterion: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  testIcon: {
    color: T.moss,
    fontWeight: '700',
    flexShrink: 0,
  },
  testText: {
    fontSize: '14px',
    color: T.ink,
  },
  criteriaList: {
    margin: 0,
    paddingLeft: '20px',
  },
  criteriaItem: {
    fontSize: '14px',
    color: T.inkMuted,
    marginBottom: '4px',
  },
  // Consultant styles
  scopeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  scopeItem: {
    padding: '16px',
    backgroundColor: T.paper,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
  },
  scopeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  scopeName: {
    fontSize: '16px',
    fontWeight: '600',
  },
  scopeDesc: {
    fontSize: '14px',
    color: T.inkMuted,
  },
  featureMetrics: {
    fontSize: '12px',
    color: T.inkMuted,
    marginLeft: 'auto',
  },
  // Requirements styles
  reqSection: {
    marginBottom: '24px',
  },
  reqSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${T.rule}`,
  },
  reqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  reqItem: {
    padding: '16px',
    backgroundColor: T.card,
    borderRadius: T.r3,
    border: `1px solid ${T.rule}`,
  },
  reqHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  reqId: {
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  reqCategory: {
    fontSize: '12px',
  },
  reqPriority: {
    fontSize: '12px',
    fontWeight: '500',
  },
  reqEditButton: {
    marginLeft: 'auto',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
  },
  reqText: {
    fontSize: '14px',
    lineHeight: '1.6',
  },
  // Consultant Capability styles - focused on what the idea DOES
  scopeSummary: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  scopeBadge: {
    fontSize: '14px',
    padding: '8px 16px',
    backgroundColor: T.paperDeep,
    border: `1px solid ${T.rule}`,
    borderRadius: T.full,
  },
  capabilityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  capabilityCard: {
    padding: '20px',
    backgroundColor: T.card,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    boxShadow: T.sh1,
  },
  capabilityHeader: {
    marginBottom: '8px',
  },
  capabilityName: {
    fontSize: '18px',
    fontWeight: '600',
    color: T.orange,
  },
  capabilityDesc: {
    fontSize: '16px',
    color: T.ink,
    lineHeight: '1.6',
    marginBottom: '16px',
  },
  capabilitySection: {
    marginTop: '12px',
  },
  capabilitySectionTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: T.inkMuted,
    marginBottom: '8px',
  },
  capabilityActionList: {
    margin: 0,
    paddingLeft: '20px',
    listStyleType: 'disc',
  },
  capabilityAction: {
    fontSize: '14px',
    color: T.ink,
    lineHeight: '1.6',
    marginBottom: '4px',
  },
  // Proposal preview styles
  proposalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  proposalActions: {
    display: 'flex',
    gap: '8px',
  },
  proposalBtn: {
    minWidth: '100px',
  },
  proposalPreview: {
    padding: '20px',
    backgroundColor: T.paper,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
    maxHeight: '600px',
    overflowY: 'auto',
  },
  proposalH1: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '16px',
    color: T.ink,
  },
  proposalH2: {
    fontSize: '20px',
    fontWeight: '600',
    marginTop: '20px',
    marginBottom: '12px',
    color: T.orangeDark,
  },
  proposalH3: {
    fontSize: '18px',
    fontWeight: '600',
    marginTop: '16px',
    marginBottom: '8px',
    color: T.ink,
  },
  proposalPara: {
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '8px',
    color: T.ink,
  },
  proposalBoldLine: {
    fontSize: '14px',
    marginBottom: '4px',
    color: T.ink,
  },
  proposalListItem: {
    fontSize: '14px',
    lineHeight: '1.6',
    marginLeft: '20px',
    marginBottom: '4px',
    color: T.ink,
  },
  proposalHr: {
    border: 'none',
    borderTop: `1px solid ${T.rule}`,
    marginTop: '24px',
    marginBottom: '12px',
  },
  proposalFooter: {
    fontSize: '14px',
    fontStyle: 'italic',
    color: T.inkMuted,
  },
  // Sync Warning Banner styles
  syncWarningBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    marginBottom: '16px',
    backgroundColor: T.roofSoft,
    borderRadius: T.r4,
    border: `1px solid ${T.ruleStrong}`,
    gap: '16px',
    flexWrap: 'wrap',
  },
  syncWarningContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  syncWarningIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  syncWarningText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  syncWarningTitle: {
    fontWeight: '600',
    fontSize: '14px',
    color: T.ink,
  },
  syncWarningDesc: {
    fontSize: '12px',
    color: T.inkMuted,
  },
  syncWarningActions: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  // Plan Overview Sub-Tab styles
  overviewSection: {
    marginBottom: '24px',
  },
  overviewSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '12px',
    color: T.ink,
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  overviewCard: {
    padding: '16px',
    backgroundColor: T.paperDeep,
    borderRadius: T.r4,
    border: `1px solid ${T.rule}`,
  },
  overviewCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  overviewCardIcon: {
    fontSize: '1.25rem',
  },
  overviewCardTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  overviewCardValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: T.ink,
    marginBottom: '4px',
  },
  overviewCardDesc: {
    fontSize: '14px',
    color: T.inkMuted,
    lineHeight: '1.6',
  },
  metricsGrid: {
    display: 'flex',
    gap: '16px',
    marginTop: '8px',
  },
  metricItem: {
    textAlign: 'center',
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: T.orange,
  },
  metricLabel: {
    fontSize: '12px',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  featuresList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  featureBadge: {
    padding: '8px 12px',
    fontSize: '14px',
  },
  techStackGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  techStackItem: {
    padding: '12px',
    backgroundColor: T.paperDeep,
    borderRadius: T.r3,
    border: `1px solid ${T.rule}`,
  },
  techStackCategory: {
    fontSize: '12px',
    fontWeight: '600',
    color: T.inkMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  techStackValue: {
    fontSize: '14px',
    color: T.ink,
    fontWeight: '500',
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
};

export default PlanView;
